import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Badge } from './ui/badge';
import { sendQuoteRequest } from '../utils/emailService';
import { PaperPlaneTilt as Mail, User, Phone, Buildings as Building, TextAlignLeft as FileText, PaperPlaneTilt as Send, CheckCircle, WarningCircle as AlertCircle } from 'phosphor-react';
import { useToast } from '../hooks/use-toast';

export const QuoteRequestForm = ({ configuration, pricing, children, onFormSubmitted }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    projectDetails: ''
  });
  const { toast } = useToast();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name || !formData.email) {
      toast({
        variant: "destructive",
        title: "Missing Information",
        description: "Please provide your name and email address."
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await sendQuoteRequest(configuration, pricing, formData);
      
      if (result.success) {
        toast({
          title: "Quote Request Sent!",
          description: "Your quote request has been sent to our team. We'll contact you within 24 hours.",
          className: "bg-green-900 border-green-600 text-green-100"
        });
        
        if (typeof onFormSubmitted === 'function') onFormSubmitted();
        setIsOpen(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          projectDetails: ''
        });
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      console.error('Quote request error:', error);
      toast({
        variant: "destructive",
        title: "Error Sending Request",
        description: error?.message || "Failed to send quote request. Please try again or call us at 519-448-4800."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      
      <DialogContent className="bg-slate-800 border-slate-600 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Mail className="w-6 h-6 text-blue-400" />
            Request Your Custom Bridge Quote
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          
          {/* Configuration Summary */}
          <Card className="border-slate-600 bg-slate-700">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg text-slate-200">Your Bridge Configuration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-400">Length:</span>
                  <span className="ml-2 text-slate-200 font-medium">{configuration.length} feet</span>
                </div>
                <div>
                  <span className="text-slate-400">Truss:</span>
                  <Badge className="ml-2 bg-blue-600 text-white">{configuration.trussType}</Badge>
                </div>
                <div>
                  <span className="text-slate-400">Material:</span>
                  <Badge className="ml-2 bg-blue-600 text-white">{configuration.material}</Badge>
                </div>
                <div>
                  <span className="text-slate-400">Railing:</span>
                  <Badge className="ml-2 bg-blue-600 text-white">{configuration.railing}</Badge>
                </div>
                <div>
                  <span className="text-slate-400">Decking:</span>
                  <Badge className="ml-2 bg-blue-600 text-white">{configuration.decking}</Badge>
                </div>
                <div>
                  <span className="text-slate-400">Add-ons:</span>
                  <span className="ml-2 text-slate-200">{configuration.addOns.length || 'None'}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-slate-300 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Full Name *
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="bg-slate-700 border-slate-600 text-slate-200 focus:border-blue-400"
                  placeholder="Enter your full name"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-300 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email Address *
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="bg-slate-700 border-slate-600 text-slate-200 focus:border-blue-400"
                  placeholder="your.email@company.com"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-slate-300 flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Phone Number
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="bg-slate-700 border-slate-600 text-slate-200 focus:border-blue-400"
                  placeholder="(555) 123-4567"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="company" className="text-slate-300 flex items-center gap-2">
                  <Building className="w-4 h-4" />
                  Company/Organization
                </Label>
                <Input
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  className="bg-slate-700 border-slate-600 text-slate-200 focus:border-blue-400"
                  placeholder="Your company name"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="projectDetails" className="text-slate-300 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Project Details & Requirements
              </Label>
              <Textarea
                id="projectDetails"
                name="projectDetails"
                value={formData.projectDetails}
                onChange={handleInputChange}
                className="bg-slate-700 border-slate-600 text-slate-200 focus:border-blue-400 min-h-[100px]"
                placeholder="Tell us about your project: location, timeline, special requirements, load capacity needs, etc."
              />
            </div>

            {/* Submission Actions */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white font-semibold py-3"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Sending Quote Request...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Quote Request
                  </>
                )}
              </Button>
              
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsOpen(false)}
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                Cancel
              </Button>
            </div>

            {/* Contact Information */}
            <div className="bg-slate-700 rounded-lg p-4 text-sm">
              <h4 className="text-slate-200 font-medium mb-2 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                What happens next?
              </h4>
              <div className="space-y-1 text-slate-400">
                <div>• Our engineering team will review your requirements</div>
                <div>• We'll contact you within 24 hours to discuss your project</div>
                <div>• Site visit will be scheduled if needed</div>
                <div>• Detailed quote with engineering drawings provided</div>
              </div>
              
              <div className="mt-3 pt-3 border-t border-slate-600">
                <div className="text-slate-300">
                  <strong>Need immediate assistance?</strong>
                </div>
                <div className="text-slate-400">
                  Call us at <span className="text-blue-400 font-medium">519-448-4800</span> or email 
                  <span className="text-blue-400 font-medium"> sales@365isi.com</span>
                </div>
              </div>
            </div>

          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};